import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Paper,
  Divider,
  TextField,
  InputAdornment,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { FormattedMessage } from 'react-intl';

import QuestionsContainer from './QuestionsContainer';
import { TYPE, REQUIRE_OPTIONS } from './types';
import { useTrialHasStarted } from './useTrialHasStarted';

const SectionEditor = ({
  section, onRemove, onUpdate, show,
}) => {
  const {
    id, questions, contextQuestions, numCases, headline,
  } = section;
  const [newMainQuestions, setNewMainQuestion] = useState(questions || []);
  const [newContextQuestions, setNewContextQuestion] = useState(contextQuestions || []);
  const [newNumCases, setNewNumCases] = useState(numCases || 10);
  const [sectionHeadline, setSectionHeadline] = useState(headline || undefined);
  const trialHasStarted = useTrialHasStarted();
  // eslint-disable-next-line no-confusing-arrow
  const getQuestion = (type) => type === TYPE.MAIN ? newMainQuestions : newContextQuestions;
  // eslint-disable-next-line no-confusing-arrow
  const getSetMethod = (type) => type === TYPE.MAIN ? setNewMainQuestion : setNewContextQuestion;

  const doUpdate = () => {
    const udpatedSection = {
      ...section,
      numCases: newNumCases,
      headline: sectionHeadline,
      questions: getQuestion(TYPE.MAIN),
      contextQuestions: getQuestion(TYPE.CONTEXT),
    };

    onUpdate(udpatedSection, section.id);
  };

  useEffect(doUpdate, [newMainQuestions, newContextQuestions, newNumCases, sectionHeadline]);

  // constant values
  const _EDITOR_ACTIONS = [
    // { label: 'View', color: 'primary', action: () => { } },
    { labelid: 'genericDelete', color: 'secondary', action: () => onRemove(id) },
  ];

  // helper method
  const removeQuestion = (questionType, questionId) => {
    const _questions = getQuestion(questionType);
    const updateQuestions = _questions.filter((question) => question.id !== questionId);
    const removeMethod = questionType === TYPE.MAIN ? setNewMainQuestion : setNewContextQuestion;
    removeMethod(updateQuestions);
  };

  const addQuestion = (questionType, innerType) => {
    const setMethod = getSetMethod(questionType);
    const currentQuestions = getQuestion(questionType);

    let newQuestion = {
      id: uuidv4(),
      type: innerType.id,
      question: {
        uuid: uuidv4(),
        de: 'Zum Verändern anklicken',
        en: `Click to edit question of '${innerType.text}'`,
      },
      required: true,
    };

    if (questionType === 'main') {
      newQuestion = { ...newQuestion, excludeFromMarking: false };
    }

    if (REQUIRE_OPTIONS.indexOf(innerType.id) > -1) {
      newQuestion.options = [];
    }

    setMethod([...currentQuestions, newQuestion]);
  };

  const updateQuestion = (questionType, updatedQuestion, questionId) => {
    const _questions = getQuestion(questionType);
    const setMethod = getSetMethod(questionType);
    // eslint-disable-next-line arrow-body-style
    const updateQuestions = _questions.map((question) => {
      return question.id === questionId ? updatedQuestion : question;
    });
    setMethod(updateQuestions);
  };

  const orderQuestions = (questionType, fromIndex, toIndex) => {
    const _questions = [...getQuestion(questionType)];
    const setMethod = getSetMethod(questionType);
    const newQuestionOrder = _questions.move(fromIndex, toIndex);
    setMethod(newQuestionOrder);
  };

  const onNumCasesChange = (event) => {
    let _numCases = parseInt(event.target.value, 10);
    _numCases = _numCases >= 45 ? 45 : _numCases;
    _numCases = _numCases <= 1 ? 1 : _numCases;
    setNewNumCases(_numCases);
  };

  if (!show) {
    return null;
  }

  return (
    <div className="w-100 pa2">
      <Paper elevation={3}>
        <div className="pa2">
          <TextField
            id="section-headline"
            label={<FormattedMessage id="sectioneditor.sectionheadline" />}
            value={sectionHeadline}
            onChange={(e) => setSectionHeadline(e.target.value)}
            onFocus={(e) => e.target.select()}
            variant="outlined"
            disabled={trialHasStarted}
            fullWidth
          />
        </div>
        <Divider variant="middle" />
        <QuestionsContainer
          questionType={TYPE.MAIN}
          mainQuestions={getQuestion(TYPE.MAIN)}
          addMethod={addQuestion}
          udpateMethod={updateQuestion}
          removeMethod={removeQuestion}
          orderMethod={orderQuestions}
        />
        <Divider variant="middle" />
        <QuestionsContainer
          questionType={TYPE.CONTEXT}
          mainQuestions={getQuestion(TYPE.CONTEXT)}
          addMethod={addQuestion}
          udpateMethod={updateQuestion}
          removeMethod={removeQuestion}
          orderMethod={orderQuestions}
        />
        <Divider />
        <div className="ph2 pv2">
          {_EDITOR_ACTIONS.map((action) => (
            <Button
              sx={{ mr: 2, my: 1 }}
              key={`id-${action.label}`}
              variant="contained"
              color={action.color}
              onClick={action.action}
              disabled={trialHasStarted}
            >
              <FormattedMessage id={action.labelid} />
            </Button>
          ))}
          <TextField
            style={{ width: '10%', maxWidth: '160px', minWidth: '160px' }}
            id="num-cases"
            label={<FormattedMessage id="sectioneditor.numofcases" />}
            type="number"
            value={newNumCases}
            onChange={onNumCasesChange}
            variant="outlined"
            InputProps={{
              endAdornment: <InputAdornment position="start">cases</InputAdornment>,
            }}
            disabled={trialHasStarted}
          />
        </div>
      </Paper>
    </div>
  );
};

export default SectionEditor;

SectionEditor.propTypes = {
  section: PropTypes.object.isRequired,
  show: PropTypes.bool.isRequired,
  onRemove: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};
